import React, { useState } from 'react';
import { Plus, Trash2, Building2, Users, Calendar, Clock, FileText, ChevronRight, ChevronLeft, Check, Download, Send } from 'lucide-react';

const GIORNI_SETTIMANA = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica'];

const SLOT_DURATA = ['15 min', '20 min', '30 min', '45 min', '60 min'];

export default function Onboarding() {
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({
    // Step 1 - Centro Medico
    centro: {
      nome: '',
      indirizzo: '',
      citta: '',
      cap: '',
      provincia: '',
      telefono: '',
      email: '',
      pec: '',
      partita_iva: '',
      sito_web: '',
      referente_nome: '',
      referente_ruolo: '',
      referente_telefono: '',
      referente_email: '',
      orario_apertura: '08:00',
      orario_chiusura: '20:00',
      giorni_apertura: ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì'],
      note_generali: ''
    },
    // Step 2 - Reparti
    reparti: [{ nome: '', servizi: [''], calendario_unificato: true }],
    // Step 3 - Medici
    medici: [{ nome: '', cognome: '', specializzazione: '', reparto: '', telefono: '', email: '' }],
    // Step 4 - Disponibilità e Slot
    disponibilita: [{ medico_index: 0, giorni: [], orario_inizio: '09:00', orario_fine: '17:00', pausa_inizio: '13:00', pausa_fine: '14:00', durata_slot: '30 min' }],
    // Step 5 - Configurazioni Aggiuntive
    config: {
      promemoria_sms: true,
      promemoria_email: true,
      ore_prima_promemoria: '24',
      consenti_cancellazione_online: true,
      ore_limite_cancellazione: '24',
      messaggio_benvenuto: '',
      messaggio_chiusura: '',
      lista_attesa_attiva: true,
      max_prenotazioni_giorno_medico: '20',
      numero_voip: '',
      pbx_provider: '',
      webhook_url: ''
    }
  });

  const steps = [
    { title: 'Centro Medico', icon: Building2, desc: 'Dati del centro' },
    { title: 'Reparti', icon: FileText, desc: 'Reparti e servizi' },
    { title: 'Medici', icon: Users, desc: 'Staff medico' },
    { title: 'Disponibilità', icon: Calendar, desc: 'Orari e slot' },
    { title: 'Configurazione', icon: Clock, desc: 'Impostazioni' }
  ];

  const updateCentro = (field, value) => {
    setFormData(prev => ({ ...prev, centro: { ...prev.centro, [field]: value } }));
  };

  const toggleGiornoApertura = (giorno) => {
    setFormData(prev => {
      const giorni = prev.centro.giorni_apertura.includes(giorno)
        ? prev.centro.giorni_apertura.filter(g => g !== giorno)
        : [...prev.centro.giorni_apertura, giorno];
      return { ...prev, centro: { ...prev.centro, giorni_apertura: giorni } };
    });
  };

  // Reparti helpers
  const addReparto = () => {
    setFormData(prev => ({ ...prev, reparti: [...prev.reparti, { nome: '', servizi: [''], calendario_unificato: true }] }));
  };
  const removeReparto = (idx) => {
    setFormData(prev => ({ ...prev, reparti: prev.reparti.filter((_, i) => i !== idx) }));
  };
  const updateReparto = (idx, field, value) => {
    setFormData(prev => {
      const reparti = [...prev.reparti];
      reparti[idx] = { ...reparti[idx], [field]: value };
      return { ...prev, reparti };
    });
  };
  const addServizio = (repartoIdx) => {
    setFormData(prev => {
      const reparti = [...prev.reparti];
      reparti[repartoIdx] = { ...reparti[repartoIdx], servizi: [...reparti[repartoIdx].servizi, ''] };
      return { ...prev, reparti };
    });
  };
  const updateServizio = (repartoIdx, servizioIdx, value) => {
    setFormData(prev => {
      const reparti = [...prev.reparti];
      const servizi = [...reparti[repartoIdx].servizi];
      servizi[servizioIdx] = value;
      reparti[repartoIdx] = { ...reparti[repartoIdx], servizi };
      return { ...prev, reparti };
    });
  };
  const removeServizio = (repartoIdx, servizioIdx) => {
    setFormData(prev => {
      const reparti = [...prev.reparti];
      reparti[repartoIdx] = { ...reparti[repartoIdx], servizi: reparti[repartoIdx].servizi.filter((_, i) => i !== servizioIdx) };
      return { ...prev, reparti };
    });
  };

  // Medici helpers
  const addMedico = () => {
    setFormData(prev => ({ ...prev, medici: [...prev.medici, { nome: '', cognome: '', specializzazione: '', reparto: '', telefono: '', email: '' }] }));
  };
  const removeMedico = (idx) => {
    setFormData(prev => ({ ...prev, medici: prev.medici.filter((_, i) => i !== idx) }));
  };
  const updateMedico = (idx, field, value) => {
    setFormData(prev => {
      const medici = [...prev.medici];
      medici[idx] = { ...medici[idx], [field]: value };
      return { ...prev, medici };
    });
  };

  // Disponibilità helpers
  const addDisponibilita = () => {
    setFormData(prev => ({ ...prev, disponibilita: [...prev.disponibilita, { medico_index: 0, giorni: [], orario_inizio: '09:00', orario_fine: '17:00', pausa_inizio: '13:00', pausa_fine: '14:00', durata_slot: '30 min' }] }));
  };
  const removeDisponibilita = (idx) => {
    setFormData(prev => ({ ...prev, disponibilita: prev.disponibilita.filter((_, i) => i !== idx) }));
  };
  const updateDisponibilita = (idx, field, value) => {
    setFormData(prev => {
      const disponibilita = [...prev.disponibilita];
      disponibilita[idx] = { ...disponibilita[idx], [field]: value };
      return { ...prev, disponibilita };
    });
  };
  const toggleGiornoDisp = (dispIdx, giorno) => {
    setFormData(prev => {
      const disponibilita = [...prev.disponibilita];
      const giorni = disponibilita[dispIdx].giorni.includes(giorno)
        ? disponibilita[dispIdx].giorni.filter(g => g !== giorno)
        : [...disponibilita[dispIdx].giorni, giorno];
      disponibilita[dispIdx] = { ...disponibilita[dispIdx], giorni };
      return { ...prev, disponibilita };
    });
  };

  // Config helpers
  const updateConfig = (field, value) => {
    setFormData(prev => ({ ...prev, config: { ...prev.config, [field]: value } }));
  };

  const handleExportJSON = () => {
    const blob = new Blob([JSON.stringify(formData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `onboarding_${formData.centro.nome.replace(/\s+/g, '_') || 'centro'}_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSubmit = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API_URL}/api/onboarding`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        alert('Scheda di onboarding inviata con successo!');
      } else {
        alert('Errore nell\'invio. Prova a scaricare il JSON e inviarlo manualmente.');
      }
    } catch {
      handleExportJSON();
      alert('Connessione non disponibile. Il file JSON è stato scaricato.');
    }
  };

  const renderStep0 = () => (
    <div className="space-y-6" data-testid="onboarding-step-centro">
      <div className="bg-gradient-to-r from-sky-50 to-cyan-50 rounded-xl p-5 border border-sky-100">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Informazioni Centro Medico</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-slate-600 mb-1">Nome Centro Medico *</label>
            <input type="text" value={formData.centro.nome} onChange={e => updateCentro('nome', e.target.value)}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-sky-400 focus:border-sky-400 transition-all"
              placeholder="Es. Centro Medico CFR Moncalieri" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-slate-600 mb-1">Indirizzo *</label>
            <input type="text" value={formData.centro.indirizzo} onChange={e => updateCentro('indirizzo', e.target.value)}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-sky-400 focus:border-sky-400 transition-all"
              placeholder="Via Roma 1" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Città *</label>
            <input type="text" value={formData.centro.citta} onChange={e => updateCentro('citta', e.target.value)}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-sky-400 focus:border-sky-400 transition-all"
              placeholder="Moncalieri" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">CAP</label>
              <input type="text" value={formData.centro.cap} onChange={e => updateCentro('cap', e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-sky-400 focus:border-sky-400 transition-all"
                placeholder="10024" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Provincia</label>
              <input type="text" value={formData.centro.provincia} onChange={e => updateCentro('provincia', e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-sky-400 focus:border-sky-400 transition-all"
                placeholder="TO" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Telefono *</label>
            <input type="tel" value={formData.centro.telefono} onChange={e => updateCentro('telefono', e.target.value)}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-sky-400 focus:border-sky-400 transition-all"
              placeholder="+39 011 1234567" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Email *</label>
            <input type="email" value={formData.centro.email} onChange={e => updateCentro('email', e.target.value)}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-sky-400 focus:border-sky-400 transition-all"
              placeholder="info@centromedico.it" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">PEC</label>
            <input type="email" value={formData.centro.pec} onChange={e => updateCentro('pec', e.target.value)}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-sky-400 focus:border-sky-400 transition-all"
              placeholder="centro@pec.it" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Partita IVA</label>
            <input type="text" value={formData.centro.partita_iva} onChange={e => updateCentro('partita_iva', e.target.value)}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-sky-400 focus:border-sky-400 transition-all"
              placeholder="IT12345678901" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Sito Web</label>
            <input type="url" value={formData.centro.sito_web} onChange={e => updateCentro('sito_web', e.target.value)}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-sky-400 focus:border-sky-400 transition-all"
              placeholder="https://www.centromedico.it" />
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-5 border border-amber-100">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Referente per l'Onboarding</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Nome e Cognome *</label>
            <input type="text" value={formData.centro.referente_nome} onChange={e => updateCentro('referente_nome', e.target.value)}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-amber-400 transition-all"
              placeholder="Mario Rossi" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Ruolo</label>
            <input type="text" value={formData.centro.referente_ruolo} onChange={e => updateCentro('referente_ruolo', e.target.value)}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-amber-400 transition-all"
              placeholder="Direttore Sanitario" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Telefono</label>
            <input type="tel" value={formData.centro.referente_telefono} onChange={e => updateCentro('referente_telefono', e.target.value)}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-amber-400 transition-all"
              placeholder="+39 333 1234567" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Email</label>
            <input type="email" value={formData.centro.referente_email} onChange={e => updateCentro('referente_email', e.target.value)}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-amber-400 transition-all"
              placeholder="referente@centromedico.it" />
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl p-5 border border-emerald-100">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Orari di Apertura del Centro</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Apertura</label>
            <input type="time" value={formData.centro.orario_apertura} onChange={e => updateCentro('orario_apertura', e.target.value)}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 transition-all" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Chiusura</label>
            <input type="time" value={formData.centro.orario_chiusura} onChange={e => updateCentro('orario_chiusura', e.target.value)}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 transition-all" />
          </div>
        </div>
        <label className="block text-sm font-medium text-slate-600 mb-2">Giorni di apertura</label>
        <div className="flex flex-wrap gap-2">
          {GIORNI_SETTIMANA.map(g => (
            <button key={g} type="button" onClick={() => toggleGiornoApertura(g)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                formData.centro.giorni_apertura.includes(g) ? 'bg-emerald-500 text-white shadow-sm' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
              }`}>{g.slice(0, 3)}</button>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-4" data-testid="onboarding-step-reparti">
      <div className="bg-gradient-to-r from-violet-50 to-purple-50 rounded-xl p-5 border border-violet-100 mb-4">
        <p className="text-sm text-slate-600">Inserisci tutti i reparti del centro e per ognuno i servizi/visite offerte. Indica se il reparto usa un <strong>calendario unificato</strong> (tutti i medici condividono lo stesso calendario) o <strong>separato</strong> (ogni medico ha il suo).</p>
      </div>

      {formData.reparti.map((reparto, rIdx) => (
        <div key={rIdx} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-slate-700">Reparto {rIdx + 1}</h4>
            {formData.reparti.length > 1 && (
              <button onClick={() => removeReparto(rIdx)} className="text-red-400 hover:text-red-600 transition-colors">
                <Trash2 size={18} />
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Nome Reparto *</label>
              <input type="text" value={reparto.nome} onChange={e => updateReparto(rIdx, 'nome', e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-violet-400 focus:border-violet-400 transition-all"
                placeholder="Es. Cardiologia, Odontoiatria, Ortopedia..." />
            </div>
            <div className="flex items-end">
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-slate-600">Calendario:</label>
                <button type="button" onClick={() => updateReparto(rIdx, 'calendario_unificato', true)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${reparto.calendario_unificato ? 'bg-violet-500 text-white' : 'bg-slate-100 text-slate-500'}`}>
                  Unificato
                </button>
                <button type="button" onClick={() => updateReparto(rIdx, 'calendario_unificato', false)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${!reparto.calendario_unificato ? 'bg-violet-500 text-white' : 'bg-slate-100 text-slate-500'}`}>
                  Separato per medico
                </button>
              </div>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-2">Servizi / Prestazioni offerte</label>
            <div className="space-y-2">
              {reparto.servizi.map((servizio, sIdx) => (
                <div key={sIdx} className="flex gap-2">
                  <input type="text" value={servizio} onChange={e => updateServizio(rIdx, sIdx, e.target.value)}
                    className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-violet-400 focus:border-violet-400 transition-all"
                    placeholder="Es. Visita cardiologica, ECG, Ecocardiogramma..." />
                  {reparto.servizi.length > 1 && (
                    <button onClick={() => removeServizio(rIdx, sIdx)} className="text-red-400 hover:text-red-600 px-2">
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
              <button onClick={() => addServizio(rIdx)} className="flex items-center gap-1 text-sm text-violet-600 hover:text-violet-800 font-medium mt-1">
                <Plus size={14} /> Aggiungi servizio
              </button>
            </div>
          </div>
        </div>
      ))}
      <button onClick={addReparto} className="w-full py-3 border-2 border-dashed border-violet-300 rounded-xl text-violet-600 font-medium hover:bg-violet-50 transition-colors flex items-center justify-center gap-2">
        <Plus size={18} /> Aggiungi Reparto
      </button>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4" data-testid="onboarding-step-medici">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-100 mb-4">
        <p className="text-sm text-slate-600">Inserisci tutti i medici che opereranno nel centro e associali al reparto corrispondente.</p>
      </div>

      {formData.medici.map((medico, mIdx) => (
        <div key={mIdx} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-slate-700">Medico {mIdx + 1}</h4>
            {formData.medici.length > 1 && (
              <button onClick={() => removeMedico(mIdx)} className="text-red-400 hover:text-red-600 transition-colors">
                <Trash2 size={18} />
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Nome *</label>
              <input type="text" value={medico.nome} onChange={e => updateMedico(mIdx, 'nome', e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all"
                placeholder="Mario" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Cognome *</label>
              <input type="text" value={medico.cognome} onChange={e => updateMedico(mIdx, 'cognome', e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all"
                placeholder="Rossi" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Specializzazione</label>
              <input type="text" value={medico.specializzazione} onChange={e => updateMedico(mIdx, 'specializzazione', e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all"
                placeholder="Cardiologo" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Reparto *</label>
              <select value={medico.reparto} onChange={e => updateMedico(mIdx, 'reparto', e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all bg-white">
                <option value="">-- Seleziona reparto --</option>
                {formData.reparti.filter(r => r.nome).map((r, i) => (
                  <option key={i} value={r.nome}>{r.nome}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Telefono</label>
              <input type="tel" value={medico.telefono} onChange={e => updateMedico(mIdx, 'telefono', e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all"
                placeholder="+39 333 1234567" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Email</label>
              <input type="email" value={medico.email} onChange={e => updateMedico(mIdx, 'email', e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all"
                placeholder="dott.rossi@centromedico.it" />
            </div>
          </div>
        </div>
      ))}
      <button onClick={addMedico} className="w-full py-3 border-2 border-dashed border-blue-300 rounded-xl text-blue-600 font-medium hover:bg-blue-50 transition-colors flex items-center justify-center gap-2">
        <Plus size={18} /> Aggiungi Medico
      </button>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4" data-testid="onboarding-step-disponibilita">
      <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl p-5 border border-teal-100 mb-4">
        <p className="text-sm text-slate-600">Configura gli orari di disponibilità per ogni medico: giorni della settimana, fascia oraria, pausa pranzo e durata degli slot per le visite.</p>
      </div>

      {formData.disponibilita.map((disp, dIdx) => (
        <div key={dIdx} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-slate-700">Configurazione {dIdx + 1}</h4>
            {formData.disponibilita.length > 1 && (
              <button onClick={() => removeDisponibilita(dIdx)} className="text-red-400 hover:text-red-600 transition-colors">
                <Trash2 size={18} />
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-600 mb-1">Medico *</label>
              <select value={disp.medico_index} onChange={e => updateDisponibilita(dIdx, 'medico_index', parseInt(e.target.value))}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition-all bg-white">
                {formData.medici.map((m, i) => (
                  <option key={i} value={i}>{m.nome || m.cognome ? `Dr. ${m.nome} ${m.cognome}` : `Medico ${i + 1}`}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-600 mb-2">Giorni di visita</label>
            <div className="flex flex-wrap gap-2">
              {GIORNI_SETTIMANA.map(g => (
                <button key={g} type="button" onClick={() => toggleGiornoDisp(dIdx, g)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    disp.giorni.includes(g) ? 'bg-teal-500 text-white shadow-sm' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                  }`}>{g.slice(0, 3)}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Inizio</label>
              <input type="time" value={disp.orario_inizio} onChange={e => updateDisponibilita(dIdx, 'orario_inizio', e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition-all" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Fine</label>
              <input type="time" value={disp.orario_fine} onChange={e => updateDisponibilita(dIdx, 'orario_fine', e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition-all" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Pausa da</label>
              <input type="time" value={disp.pausa_inizio} onChange={e => updateDisponibilita(dIdx, 'pausa_inizio', e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition-all" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Pausa a</label>
              <input type="time" value={disp.pausa_fine} onChange={e => updateDisponibilita(dIdx, 'pausa_fine', e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition-all" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Durata slot</label>
              <select value={disp.durata_slot} onChange={e => updateDisponibilita(dIdx, 'durata_slot', e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition-all bg-white">
                {SLOT_DURATA.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
        </div>
      ))}
      <button onClick={addDisponibilita} className="w-full py-3 border-2 border-dashed border-teal-300 rounded-xl text-teal-600 font-medium hover:bg-teal-50 transition-colors flex items-center justify-center gap-2">
        <Plus size={18} /> Aggiungi Configurazione Disponibilità
      </button>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6" data-testid="onboarding-step-config">
      <div className="bg-gradient-to-r from-rose-50 to-pink-50 rounded-xl p-5 border border-rose-100">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Notifiche e Promemoria</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center gap-3">
            <input type="checkbox" checked={formData.config.promemoria_sms} onChange={e => updateConfig('promemoria_sms', e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-rose-500 focus:ring-rose-400" />
            <label className="text-sm text-slate-600">Promemoria via SMS</label>
          </div>
          <div className="flex items-center gap-3">
            <input type="checkbox" checked={formData.config.promemoria_email} onChange={e => updateConfig('promemoria_email', e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-rose-500 focus:ring-rose-400" />
            <label className="text-sm text-slate-600">Promemoria via Email</label>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Ore prima del promemoria</label>
            <select value={formData.config.ore_prima_promemoria} onChange={e => updateConfig('ore_prima_promemoria', e.target.value)}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-rose-400 focus:border-rose-400 transition-all bg-white">
              <option value="1">1 ora prima</option>
              <option value="2">2 ore prima</option>
              <option value="12">12 ore prima</option>
              <option value="24">24 ore prima</option>
              <option value="48">48 ore prima</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-5 border border-indigo-100">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Prenotazioni Online</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center gap-3">
            <input type="checkbox" checked={formData.config.consenti_cancellazione_online} onChange={e => updateConfig('consenti_cancellazione_online', e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-indigo-500 focus:ring-indigo-400" />
            <label className="text-sm text-slate-600">Consenti cancellazione online</label>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Limite cancellazione (ore prima)</label>
            <input type="number" value={formData.config.ore_limite_cancellazione} onChange={e => updateConfig('ore_limite_cancellazione', e.target.value)}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition-all"
              placeholder="24" />
          </div>
          <div className="flex items-center gap-3">
            <input type="checkbox" checked={formData.config.lista_attesa_attiva} onChange={e => updateConfig('lista_attesa_attiva', e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-indigo-500 focus:ring-indigo-400" />
            <label className="text-sm text-slate-600">Lista d'attesa attiva</label>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Max prenotazioni/giorno per medico</label>
            <input type="number" value={formData.config.max_prenotazioni_giorno_medico} onChange={e => updateConfig('max_prenotazioni_giorno_medico', e.target.value)}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition-all"
              placeholder="20" />
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-5 border border-green-100">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Messaggi Personalizzati (Voce AI)</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Messaggio di benvenuto telefonico</label>
            <textarea value={formData.config.messaggio_benvenuto} onChange={e => updateConfig('messaggio_benvenuto', e.target.value)}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-green-400 focus:border-green-400 transition-all resize-none"
              rows={3} placeholder="Es. Buongiorno, benvenuto al Centro Medico [Nome]. Come posso aiutarla?" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Messaggio di chiusura</label>
            <textarea value={formData.config.messaggio_chiusura} onChange={e => updateConfig('messaggio_chiusura', e.target.value)}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-green-400 focus:border-green-400 transition-all resize-none"
              rows={3} placeholder="Es. Grazie per aver chiamato. Le ricordiamo l'appuntamento il [data] alle [ora]. Arrivederci!" />
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-r from-slate-50 to-gray-50 rounded-xl p-5 border border-slate-200">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Integrazione VoIP (opzionale)</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Numero VoIP</label>
            <input type="text" value={formData.config.numero_voip} onChange={e => updateConfig('numero_voip', e.target.value)}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all"
              placeholder="+39 06 12345678" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Provider PBX</label>
            <input type="text" value={formData.config.pbx_provider} onChange={e => updateConfig('pbx_provider', e.target.value)}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all"
              placeholder="Es. Robottino.ai, Twilio, Vonage..." />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Webhook URL</label>
            <input type="text" value={formData.config.webhook_url} onChange={e => updateConfig('webhook_url', e.target.value)}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all"
              placeholder="https://..." />
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-600 mb-1">Note aggiuntive per la configurazione</label>
        <textarea value={formData.centro.note_generali} onChange={e => updateCentro('note_generali', e.target.value)}
          className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all resize-none"
          rows={4} placeholder="Inserisci qui eventuali richieste particolari, integrazioni con software esistenti, esigenze specifiche..." />
      </div>
    </div>
  );

  const renderSteps = [renderStep0, renderStep1, renderStep2, renderStep3, renderStep4];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-sky-50 py-8 px-4" data-testid="onboarding-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-slate-800 mb-2">Onboarding Centro Medico</h1>
          <p className="text-slate-500">Compila questo modulo per configurare la piattaforma TelcaVoIP AI per il tuo centro</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-8 px-2">
          {steps.map((s, i) => (
            <React.Fragment key={i}>
              <button onClick={() => setStep(i)} className="flex flex-col items-center gap-1 group">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                  i === step ? 'bg-sky-500 text-white shadow-lg shadow-sky-200' :
                  i < step ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-400'
                }`}>
                  {i < step ? <Check size={18} /> : <s.icon size={18} />}
                </div>
                <span className={`text-xs font-medium hidden md:block ${i === step ? 'text-sky-600' : 'text-slate-400'}`}>{s.title}</span>
              </button>
              {i < steps.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 ${i < step ? 'bg-emerald-400' : 'bg-slate-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step Content */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-slate-100 p-6 md:p-8">
          <div className="flex items-center gap-3 mb-6">
            {React.createElement(steps[step].icon, { size: 24, className: 'text-sky-500' })}
            <div>
              <h2 className="text-xl font-bold text-slate-800">{steps[step].title}</h2>
              <p className="text-sm text-slate-400">{steps[step].desc}</p>
            </div>
          </div>

          {renderSteps[step]()}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6">
          <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-all ${
              step === 0 ? 'text-slate-300 cursor-not-allowed' : 'text-slate-600 hover:bg-slate-100'
            }`}>
            <ChevronLeft size={18} /> Indietro
          </button>

          <div className="flex gap-3">
            {step === steps.length - 1 && (
              <>
                <button onClick={handleExportJSON}
                  className="flex items-center gap-2 px-5 py-2.5 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200 transition-all"
                  data-testid="onboarding-export-btn">
                  <Download size={18} /> Scarica JSON
                </button>
                <button onClick={handleSubmit}
                  className="flex items-center gap-2 px-6 py-2.5 bg-emerald-500 text-white rounded-xl font-medium hover:bg-emerald-600 shadow-lg shadow-emerald-200 transition-all"
                  data-testid="onboarding-submit-btn">
                  <Send size={18} /> Invia Scheda
                </button>
              </>
            )}
            {step < steps.length - 1 && (
              <button onClick={() => setStep(step + 1)}
                className="flex items-center gap-2 px-6 py-2.5 bg-sky-500 text-white rounded-xl font-medium hover:bg-sky-600 shadow-lg shadow-sky-200 transition-all"
                data-testid="onboarding-next-btn">
                Avanti <ChevronRight size={18} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
